// v2.15.5 — Page publique landing pour la demande d'adresse (Vue A/B/C).
// Pas d'auth. Token consommé une seule fois.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import MiniMapPreview from "@/components/MiniMapPreview";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const CI_BBOX = { latMin: 4.0, latMax: 11.0, lngMin: -9.0, lngMax: -2.0 };
function isInCI(lat, lng) {
  return Number.isFinite(lat) && Number.isFinite(lng)
    && lat >= CI_BBOX.latMin && lat <= CI_BBOX.latMax
    && lng >= CI_BBOX.lngMin && lng <= CI_BBOX.lngMax;
}

export default function AddressRequestLandingPage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);  // {requester_name, contact_name_hint, status, expires_at}
  const [error, setError] = useState(null);
  const [view, setView] = useState("loading"); // loading | A | B | C | invalid
  const [gpsCoords, setGpsCoords] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [encoded, setEncoded] = useState(null); // {address_civ, commune_name, city_name, lat, lng}
  const [contactName, setContactName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resultCode, setResultCode] = useState(null);

  useEffect(() => {
    axios.get(`${API}/v3/address-requests/${token}`).then((r) => {
      const d = r.data;
      if (!d?.ok) { setError("invalid"); setView("invalid"); return; }
      if (d.status === "expired") { setError("Lien expiré"); setView("invalid"); return; }
      if (d.status === "fulfilled") { setError("Adresse déjà partagée"); setView("invalid"); return; }
      setMeta(d);
      setContactName(d.contact_name_hint || "");
      setView("A");
    }).catch(() => { setError("Lien introuvable"); setView("invalid"); });
  }, [token]);

  const startGpsCapture = () => {
    setView("B");
    setGpsError(null);
    if (!navigator.geolocation) { setGpsError("GPS non disponible sur ton navigateur"); return; }
    // Délai mini 1.2s pour l'effet "calcul en cours" même si fix GPS rapide.
    const t0 = Date.now();
    navigator.geolocation.getCurrentPosition(async (p) => {
      const lat = p.coords.latitude, lng = p.coords.longitude;
      if (!isInCI(lat, lng)) { setGpsError("Tu sembles ne pas être en Côte d'Ivoire. Le code métrique ne peut être calculé que sur le territoire ivoirien."); return; }
      setGpsCoords({ lat, lng });
      try {
        const r = await axios.post(`${API}/v3/addresses/from-geoloc`, { lat, lng });
        if (!r.data?.ok) { setGpsError(r.data?.reason || "Erreur de localisation"); return; }
        // Délai mini total ~1.2s pour UX cohérente.
        const wait = Math.max(0, 1200 - (Date.now() - t0));
        setTimeout(() => setEncoded(r.data.preview), wait);
      } catch (e) {
        setGpsError(e.response?.data?.reason || e.message);
      }
    }, (err) => {
      setGpsError(`Accès GPS refusé : ${err.message}. Recharge la page et autorise la localisation.`);
    }, { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 });
  };

  const submitFulfill = async () => {
    if (!encoded || !contactName.trim()) { toast.error("Donne ton nom avant d'envoyer"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/v3/address-requests/${token}/fulfill`, {
        lat: encoded.lat, lng: encoded.lng, contact_name: contactName.trim(),
      });
      if (r.data?.ok) {
        setResultCode(r.data.address_civ);
        setView("C");
      } else {
        toast.error(r.data?.reason || "Erreur");
      }
    } catch (e) {
      toast.error(e.response?.data?.reason || e.message);
    } finally { setSubmitting(false); }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(resultCode); toast.success("✅ Code copié"); }
    catch { toast.error("Copie échouée"); }
  };

  if (view === "loading") return <div style={fullScreen}><div style={{ color: "#94a3b8" }}>Chargement…</div></div>;
  if (view === "invalid") {
    return (
      <div style={fullScreen}>
        <div style={card}>
          <h2 style={{ margin: 0, fontSize: 22 }}>❌ Lien invalide</h2>
          <p style={{ color: "#94a3b8", marginTop: 8 }}>{error || "Ce lien n'est plus valide."}</p>
          <a href="/" style={ctaPrimary}>Découvrir CIV-AI</a>
        </div>
      </div>
    );
  }

  return (
    <div style={fullScreen}>
      <div style={card}>
        {view === "A" && (
          <div data-testid="request-view-a">
            <h2 style={{ margin: 0, fontSize: 22 }}>👋 {meta.requester_name || "Un contact"} vous demande votre adresse métrique sécurisée <span style={{ color: "#FF7E1B" }}>CIV-AI</span></h2>
            <p style={{ color: "#cbd5e1", marginTop: 12, fontSize: 14 }}>
              Pour que vos livraisons et courriers arrivent pile devant votre porte, respectez ces 3 consignes :
            </p>
            <ol style={{ color: "#e2e8f0", lineHeight: 1.6, fontSize: 13.5, paddingLeft: 0, listStyle: "none", marginTop: 14 }}>
              <li style={step}>🏠 <strong>Étape 1 : Soyez sur place</strong><br /><span style={stepHint}>Remplissez uniquement quand vous êtes physiquement chez vous.</span></li>
              <li style={step}>📡 <strong>Étape 2 : Activez votre GPS</strong><br /><span style={stepHint}>Pas en mode économie d'énergie.</span></li>
              <li style={step}>🔓 <strong>Étape 3 : Autorisez le navigateur</strong><br /><span style={stepHint}>Cliquez "Autoriser" quand demandé.</span></li>
            </ol>
            <button data-testid="request-start-gps-btn" onClick={startGpsCapture} style={ctaPrimary}>
              📍 Je suis chez moi, générer mon adresse
            </button>
          </div>
        )}

        {view === "B" && (
          <div data-testid="request-view-b">
            {!encoded && !gpsError && (
              <div style={{ textAlign: "center", padding: 30 }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🎯</div>
                <div style={{ color: "#cbd5e1" }}>Calcul de la position métrique en cours…</div>
                <div style={spinner} />
              </div>
            )}
            {gpsError && (
              <div data-testid="request-gps-error">
                <h3 style={{ margin: 0, color: "#FCA5A5" }}>⚠️ {gpsError}</h3>
                <button onClick={() => window.location.reload()} style={ctaPrimary}>🔄 Recharger</button>
              </div>
            )}
            {encoded && (
              <div data-testid="request-preview">
                <h3 style={{ margin: 0, fontSize: 18 }}>✅ Position calculée</h3>
                <div style={{ background: "rgba(59,130,246,0.15)", padding: "8px 12px", borderRadius: 8, margin: "10px 0", color: "#93C5FD", fontWeight: 700, fontFamily: "monospace" }}>
                  {encoded.address_civ}
                </div>
                <div style={{ fontSize: 13, color: "#cbd5e1", marginBottom: 8 }}>
                  📍 {encoded.commune_name}, {encoded.city_name}
                </div>
                <div style={{ marginBottom: 12, borderRadius: 8, overflow: "hidden" }}>
                  <MiniMapPreview lat={encoded.lat} lng={encoded.lng} onSet={() => {}} height={180} />
                </div>
                <label style={lbl}>Votre nom (visible par {meta.requester_name || "le demandeur"})</label>
                <input data-testid="request-contact-name" type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Ex: Maman, Koffi..." style={inp} />
                <button data-testid="request-submit-btn" onClick={submitFulfill} disabled={submitting || !contactName.trim()} style={{ ...ctaPrimary, opacity: (submitting || !contactName.trim()) ? 0.6 : 1 }}>
                  {submitting ? "..." : "👍 Envoyer mon adresse"}
                </button>
              </div>
            )}
          </div>
        )}

        {view === "C" && (
          <div data-testid="request-view-c">
            <h2 style={{ margin: 0, fontSize: 22 }}>✅ Ton adresse a été envoyée à {meta.requester_name || "ton contact"}</h2>
            <div style={{ background: "rgba(34,197,94,0.12)", padding: "12px 14px", borderRadius: 8, margin: "14px 0", color: "#86EFAC", fontWeight: 700, fontFamily: "monospace", fontSize: 16, textAlign: "center" }}>
              {resultCode}
            </div>
            <button data-testid="request-copy-code" onClick={copyCode} style={ctaSecondary}>📋 Copier mon code</button>
            <p style={{ color: "#94a3b8", marginTop: 14, fontSize: 12 }}>
              💡 Tu peux installer CIV-AI pour gérer tes propres adresses : <a href="/" style={{ color: "#FBBF24" }}>Découvrir l'app</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const fullScreen = { minHeight: "100vh", background: "#0F172A", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, color: "#F1F5F9" };
const card = { background: "#1E293B", borderRadius: 12, padding: 24, maxWidth: 480, width: "100%", border: "1px solid rgba(255,255,255,0.08)" };
const step = { background: "rgba(255,255,255,0.04)", padding: "10px 12px", borderRadius: 8, marginBottom: 8 };
const stepHint = { color: "#94a3b8", fontSize: 11 };
const lbl = { display: "block", fontSize: 11, color: "#94a3b8", marginTop: 8, marginBottom: 4 };
const inp = { width: "100%", padding: 10, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 6, fontSize: 14, boxSizing: "border-box", marginBottom: 12 };
const ctaPrimary = { display: "block", width: "100%", textAlign: "center", padding: "12px 16px", background: "linear-gradient(135deg,#F59E0B,#FBBF24)", color: "#0F172A", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, marginTop: 14, textDecoration: "none", boxSizing: "border-box" };
const ctaSecondary = { display: "block", width: "100%", textAlign: "center", padding: "10px 14px", background: "rgba(255,255,255,0.08)", color: "#CBD5E1", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const spinner = { width: 28, height: 28, margin: "16px auto 0", border: "3px solid rgba(245,158,11,0.25)", borderTopColor: "#F59E0B", borderRadius: "50%", animation: "spin 1s linear infinite" };
